import { LitElement, html, css, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { overrideDesignTokens } from '../../shared/styles/override-design-tokens';
import '../icon/icon';
import resetStyle from '../../shared/components/html-content/reset.style';
import { ludsBaseFont, ludsTypographyFontWeight } from '../../shared/styles/typography';

@customElement('luds-progress')
export class LudsProgress extends LitElement {
  static override styles = [
    overrideDesignTokens,
    resetStyle,
    ludsBaseFont,
    ludsTypographyFontWeight,
    css`
      :host {
        --luds-progress-bar-success-background-color: var(--semantic-success);
        --luds-progress-bar-error-background-color: var(--colours-extended-palette-reds-red-700);
        --luds-progress-bar-success-icon-color: var(--colours-extended-palette-greens-green-900);
        --luds-progress-bar-error-icon-color: var(--colours-extended-palette-reds-red-900);
        --luds-progress-bar-label-color: var(--colours-neutrals-gray-800);
        --luds-progress-bar-border-radius: var(--corners-s);
      }

      progress {
        border-radius: var(--luds-progress-bar-border-radius);
        width: 100%;
        height: 4px;
      }

      progress[value] {
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        border: none;
      }

      progress::-webkit-progress-bar {
        background-color: var(--colours-neutrals-gray-300);
        border-radius: var(--luds-progress-bar-border-radius);
      }

      progress::-moz-progress-bar {
        background-color: var(--colours-neutrals-gray-300);
        border-radius: var(--luds-progress-bar-border-radius);
      }

      :host([variant='success']) progress::-webkit-progress-value {
        background-color: var(--luds-progress-bar-success-background-color);
        border-radius: var(--luds-progress-bar-border-radius);
      }

      :host([variant='error']) progress::-webkit-progress-value {
        background-color: var(--luds-progress-bar-error-background-color);
        border-radius: var(--luds-progress-bar-border-radius);
      }

      :host([variant='success']) progress::-moz-progress-value {
        background-color: var(--luds-progress-bar-success-background-color);
        border-radius: var(--luds-progress-bar-border-radius);
      }

      :host([variant='error']) progress::-moz-progress-value {
        background-color: var(--luds-progress-bar-error-background-color);
        border-radius: var(--luds-progress-bar-border-radius);
      }

      :host([variant='success']) {
        & luds-icon {
          fill: var(--luds-progress-bar-success-icon-color);
        }
      }

      :host([variant='error']) {
        & luds-icon {
          fill: var(--luds-progress-bar-error-icon-color);
        }
      }

      .status-label {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: var(--spacing-spacing-08);
        margin-top: var(--spacing-spacing-08);
      }

      :host([size='small']) .status-label {
        gap: var(--spacing-spacing-04);
      }

      .progress-label {
        margin-top: 0;
        color: var(--luds-progress-bar-label-color);
        font-size: 14px;
        line-height: 16px;
        font-weight: 600;
      }

      :host([size='small']) .progress-label {
        font-size: 12px;
        font-weight: 400;
      }
    `,
  ];

  //** Specifies current progress completion status value */
  @property({ type: Number })
  value = 0;

  //** Specifies value to determine completion. Default value is 100 */
  @property({ type: Number })
  max = 100;

  //** Specifies the label value bellow progress bar */
  @property({ type: String })
  label = '';

  //** Defines status icon visibility */
  @property({ type: Boolean })
  showIcon = false;

  //**  Specifies variant for progress bar, this affects the progress bar and icon colors */
  @property({ type: String, reflect: true }) variant: 'success' | 'error' = 'success';

  //**  Specifies size for progress bar, this affects the label and icon sizes */
  @property({ type: String, reflect: true }) size: 'small' | 'large' = 'large';

  override render() {
    return html`<progress
        id="luds-progress"
        value="${this.value}"
        max="${this.max}"
        aria-valuemin="0"
        aria-valuenow="${this.value}"
        aria-valuemax="${this.max}"
        aria-label="${this.label}"></progress>
      <div class="status-label">
        ${this.showIcon
          ? html`<luds-icon
              icon="${this.variant === 'success' ? 'completed' : 'canceled'}"
              size="${this.size === 'large' ? 'large' : 'small'}"
              aria-hidden="true"></luds-icon>`
          : ''}
        ${this.label ? html`<label for="luds-progress" class="progress-label">${this.label}</label>` : nothing}
      </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'luds-progress': LudsProgress;
  }
}
